import type { ReactNode } from 'react'
import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { HiddenSimulationWidget } from '@/components/admin/HiddenSimulationWidget'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { ErrorState, LoadingState } from '@/components/common/StateViews'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { useBootstrap } from '@/hooks/useBootstrap'
import { atLeast, type NavItem } from '@/components/layout/nav'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/store'

// Route-level code splitting keeps the first paint small on mobile data.
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const PlansPage = lazy(() => import('@/pages/PlansPage'))
const PlanDetailPage = lazy(() => import('@/pages/PlanDetailPage'))
const SongsPage = lazy(() => import('@/pages/SongsPage'))
const SongDetailPage = lazy(() => import('@/pages/SongDetailPage'))
const SongPrintPage = lazy(() => import('@/pages/SongPrintPage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const TeamsPage = lazy(() => import('@/pages/TeamsPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const NotificationSettingsPage = lazy(() => import('@/pages/NotificationSettingsPage'))
const AccountPage = lazy(() => import('@/pages/AccountPage'))
const AdminPage = lazy(() => import('@/pages/AdminPage'))
const RehearsePage = lazy(() => import('@/pages/RehearsePage'))
const SongBuilderPage = lazy(() => import('@/pages/SongBuilderPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const SignInPage = lazy(() => import('@/pages/SignInPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))

/**
 * Sends anyone below `min` back to the dashboard rather than showing a wall
 * of 403s.
 *
 * This is NOT the security boundary — every one of these routes is enforced
 * again in PHP. It exists so a member who follows an old link lands somewhere
 * sensible instead of on a screen of failed requests.
 *
 * Gated on ROLE only, never on viewport. Volunteers and Teams are off the
 * phone's navigation (see `desktopOnly` in nav.ts), but a scheduler who taps
 * a link to one in an email should still arrive rather than be bounced home.
 */
function RequireRole({ min, children }: { min: NavItem['minRole']; children: ReactNode }) {
  // Through usePermissions, not the store directly: the nav, the page bodies
  // and these guards must agree about what role this is, and two readers of
  // the same field drift the moment one of them grows a default the other
  // does not have.
  const { role } = usePermissions()
  return atLeast(role, min) ? <>{children}</> : <Navigate to="/" replace />
}

export default function App() {
  const { state, error } = useBootstrap()
  const authStatus = useAuthStore((s) => s.status)
  const completeSso = useAuthStore((s) => s.completeSso)
  const [showForgot, setShowForgot] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)

  /*
   * Finish a "Sign in with MyMDV" round trip.
   *
   * The provider redirects back to `/?sso=<one-time code>`. It is traded for a
   * real token immediately and stripped from the URL with replaceState, so it
   * never reaches the browser's history or a bookmark.
   *
   * Nothing in the app starts that round trip any more: Ian had the button
   * removed on 2026-10-26 and the silent attempt that used to sit below this
   * went with it, because the sign-in form now takes a MyMDV password
   * directly. This stayed so a redirect already in flight when that shipped,
   * or a bookmarked callback, finishes instead of landing on a blank app.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('sso')
    const failure = params.get('sso_error')

    if (failure) {
      setSsoError(failure)
      window.history.replaceState({}, '', window.location.pathname)
      return
    }
    if (!code) return

    window.history.replaceState({}, '', window.location.pathname)
    void completeSso(code).then((ok) => {
      if (!ok) setSsoError('That sign-in could not be completed. Try again.')
    })
  }, [completeSso])

  /*
   * A reset link has to work whatever the session says — including for
   * somebody already signed in on another device, and before the bootstrap
   * has decided who they are.
   */
  const onResetRoute = window.location.pathname.replace(/\/+$/, '').endsWith('/reset-password')

  if (state === 'starting') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState label="Opening your local database…" />
      </div>
    )
  }

  if (state === 'failed') {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg items-center p-6">
        <ErrorState
          title="Services could not start"
          message={
            error ??
            'The local database could not be opened. Private browsing windows block IndexedDB in some browsers.'
          }
          onRetry={() => window.location.reload()}
        />
      </div>
    )
  }

  if (onResetRoute) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Loading…" />}>
          <ResetPasswordPage
            onDone={() => {
              // Straight to the door: the reset revoked every token, so
              // whatever session this tab had is gone.
              window.location.replace(import.meta.env.BASE_URL)
            }}
          />
        </Suspense>
      </ErrorBoundary>
    )
  }

  // Not signed in: the only thing rendered is the door.
  if (authStatus === 'anonymous' || authStatus === 'setup') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Loading…" />}>
          {showForgot && authStatus === 'anonymous' ? (
            <ForgotPasswordPage onBack={() => setShowForgot(false)} />
          ) : (
            <SignInPage
              mode={authStatus === 'setup' ? 'setup' : 'signin'}
              onForgotPassword={() => setShowForgot(true)}
              ssoError={ssoError}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <Routes>
        <Route element={<AppShell />}>
          <Route
            index
            element={
              <Suspense fallback={<LoadingState />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route path="plans" element={<Suspense fallback={<LoadingState />}><PlansPage /></Suspense>} />
          <Route path="plans/:planId" element={<Suspense fallback={<LoadingState />}><PlanDetailPage /></Suspense>} />
          <Route path="songs" element={<Suspense fallback={<LoadingState />}><SongsPage /></Suspense>} />
          <Route path="songs/:songId" element={<Suspense fallback={<LoadingState />}><SongDetailPage /></Suspense>} />
          <Route path="songs/:songId/print" element={<Suspense fallback={<LoadingState />}><SongPrintPage /></Suspense>} />
          <Route path="songs/:songId/builder" element={<Suspense fallback={<LoadingState />}><SongBuilderPage /></Suspense>} />
          <Route path="songs/:songId/rehearse" element={<Suspense fallback={<LoadingState />}><RehearsePage /></Suspense>} />
          <Route path="plans/:planId/rehearse" element={<Suspense fallback={<LoadingState />}><RehearsePage /></Suspense>} />
          <Route
            path="schedule"
            element={
              <RequireRole min="scheduler">
                <Suspense fallback={<LoadingState />}><SchedulePage /></Suspense>
              </RequireRole>
            }
          />
          <Route
            path="teams"
            element={
              <RequireRole min="scheduler">
                <Suspense fallback={<LoadingState />}><TeamsPage /></Suspense>
              </RequireRole>
            }
          />
          <Route path="notifications" element={<Suspense fallback={<LoadingState />}><NotificationsPage /></Suspense>} />
          <Route path="notifications/settings" element={<Suspense fallback={<LoadingState />}><NotificationSettingsPage /></Suspense>} />
          <Route path="account" element={<Suspense fallback={<LoadingState />}><AccountPage /></Suspense>} />
          <Route path="people" element={<Navigate to="/teams" replace />} />
          {/*
            Typing /admin should not open the admin screen for a member. The
            endpoints behind it refuse them anyway, but a page full of
            permission errors is not an answer — send them home instead.
          */}
          <Route
            path="admin"
            element={
              <RequireRole min="admin">
                <Suspense fallback={<LoadingState />}><AdminPage /></Suspense>
              </RequireRole>
            }
          />
          <Route path="*" element={<Suspense fallback={<LoadingState />}><NotFoundPage /></Suspense>} />
        </Route>
      </Routes>
      <HiddenSimulationWidget />
    </ErrorBoundary>
  )
}
